import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { DatePicker } from '@/components/ui/date-picker';
import { WysiwygEditor } from '@/components/WysiwygEditor';
import { ImageGalleryUpload } from '@/components/ImageGalleryUpload';
import { PdfUpload } from '@/components/PdfUpload';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, Upload, X, Image as ImageIcon, Eye, Code, Loader2, Copy, ExternalLink, Check, ChevronsUpDown, Search } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EventEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const isNew = !id || id === 'new';
  
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [attachments, setAttachments] = useState([]);
  
  const [formData, setFormData] = useState({
    title: '',
    lead: '',
    content: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    time: '',
    location: '',
    link: '',
    link_text: 'Mehr dazu',
    image: '',
    // Booking fields
    max_participants: '',
    registration_open: true,
    registration_deadline: '',
    price: '',
    price_label: '',
    payment_required: false,
    guest_registration_enabled: false,
    organizer_id: '',
    // Visibility fields
    visible_members: true,
    visible_public: true,
    // Rückblick fields
    has_review: false,
    review_text: '',
    review_images: [],
    review_pdfs: []
  });
  const [members, setMembers] = useState([]);

  const linkTextOptions = [
    { value: 'Mehr dazu', label: 'Mehr dazu' },
    { value: 'Jetzt anmelden', label: 'Jetzt anmelden' },
    { value: 'Zur Anmeldung', label: 'Zur Anmeldung' },
    { value: 'Weitere Infos', label: 'Weitere Infos' },
    { value: 'Tickets kaufen', label: 'Tickets kaufen' }
  ];

  const fileInputRef = useRef(null);
  const getToken = () => localStorage.getItem('shrt_token');

  useEffect(() => {
    if (!isAdmin) {
      navigate('/events');
      return;
    }
    if (!isNew) {
      fetchEvent();
    }
    fetchMembers();
  }, [id, isAdmin]);

  const fetchMembers = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API_URL}/api/members?limit=200`, { headers: { Authorization: `Bearer ${token}` } });
      const list = (response.data.members || []).map(m => {
        const f = m.fields || {};
        const name = `${(f.Vorname?.text || '')} ${(f.Name?.text || '')}`.trim();
        return { id: m.id, name: name || m.email };
      }).sort((a, b) => a.name.localeCompare(b.name));
      setMembers(list);
    } catch (e) { /* ignore */ }
  };

  const fetchEvent = async () => {
    const token = getToken();
    try {
      const response = await axios.get(
        `${API_URL}/api/events/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFormData({
        title: response.data.title || '',
        lead: response.data.lead || '',
        content: response.data.content || response.data.body_html || '',
        description: response.data.description || '',
        date: response.data.date || '',
        time: response.data.time || '',
        location: response.data.location || '',
        link: response.data.link || '',
        link_text: response.data.link_text || 'Mehr dazu',
        image: response.data.image || response.data.image_url || '',
        // Booking fields
        max_participants: response.data.max_participants || '',
        registration_open: response.data.registration_open !== false,
        registration_deadline: response.data.registration_deadline || '',
        price: response.data.price || '',
        price_label: response.data.price_label || '',
        payment_required: response.data.payment_required || false,
        guest_registration_enabled: response.data.guest_registration_enabled || false,
        organizer_id: response.data.organizer_id || '',
        // Visibility fields
        visible_members: response.data.visible_members !== false,
        visible_public: response.data.visible_public !== false,
        // Rückblick fields
        has_review: response.data.has_review || false,
        review_text: response.data.review_text || '',
        review_images: response.data.review_images || [],
        review_pdfs: response.data.review_pdfs || []
      });
      setAttachments(response.data.attachments || []);
    } catch (error) {
      toast.error('Event nicht gefunden');
      navigate('/events');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Bild darf maximal 5MB gross sein');
      return;
    }

    setUploading(true);
    const token = getToken();

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      // Upload to Cloudflare R2
      const response = await axios.post(
        `${API_URL}/api/admin/upload/r2?folder=events`,
        formDataUpload,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );

      setFormData(prev => ({ ...prev, image: response.data.url }));
      toast.success('Bild hochgeladen (Cloudflare R2)');
    } catch (error) {
      toast.error('Fehler beim Hochladen');
    } finally {
      setUploading(false);
    }
  };

  const handleAttachmentUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    const token = getToken();
    setUploadingAttachment(true);
    
    for (const file of files) {
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name}: Nur PDF und Bilder erlaubt`);
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name}: Maximal 10MB`);
        continue;
      }
      
      try {
        const fd = new FormData();
        fd.append('file', file);
        const response = await axios.post(
          `${API_URL}/api/events/${id}/attachments`,
          fd,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
        );
        setAttachments(prev => [...prev, response.data.attachment]);
        toast.success(`${file.name} hochgeladen`);
      } catch (error) {
        toast.error(`${file.name}: ${error.response?.data?.detail || 'Upload fehlgeschlagen'}`);
      }
    }
    setUploadingAttachment(false);
    e.target.value = '';
  };

  const handleDeleteAttachment = async (attachmentId, filename) => {
    if (!window.confirm(`"${filename}" wirklich löschen?`)) return;
    const token = getToken();
    try {
      await axios.delete(
        `${API_URL}/api/events/${id}/attachments/${attachmentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAttachments(prev => prev.filter(a => a.id !== attachmentId));
      toast.success('Datei gelöscht');
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.date) {
      toast.error('Titel und Datum sind erforderlich');
      return;
    }

    if (!formData.organizer_id) {
      toast.error('Organisator (Kontaktperson) ist ein Pflichtfeld');
      return;
    }

    setSaving(true);
    const token = getToken();

    // Prepare data with correct field names
    const eventData = {
      title: formData.title,
      lead: formData.lead,
      body_html: formData.content,
      date: formData.date,
      time: formData.time,
      location: formData.location,
      link: formData.link,
      link_text: formData.link_text,
      image_url: formData.image,
      max_participants: formData.max_participants ? parseInt(formData.max_participants) : null,
      registration_open: formData.registration_open,
      registration_deadline: formData.registration_deadline || null,
      price: formData.price ? parseFloat(formData.price) : 0,
      price_label: formData.price_label,
      payment_required: formData.payment_required,
      guest_registration_enabled: formData.guest_registration_enabled,
      organizer_id: formData.organizer_id || null,
      // Visibility fields
      visible_members: formData.visible_members,
      visible_public: formData.visible_public,
      // Rückblick fields
      has_review: formData.has_review,
      review_text: formData.review_text || null,
      review_images: formData.review_images || [],
      review_pdfs: formData.review_pdfs || []
    };

    try {
      if (isNew) {
        const response = await axios.post(
          `${API_URL}/api/events`,
          eventData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Event erstellt');
        navigate(`/events/${response.data.id}`);
      } else {
        await axios.put(
          `${API_URL}/api/events/${id}`,
          eventData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Event aktualisiert');
        navigate(`/events/${id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className=" flex items-center justify-center min-h-[50vh]">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className=" max-w-4xl">
        {/* Back Button */}
        <button 
          onClick={() => navigate(isNew ? '/events' : `/events/${id}`)}
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
        >
          <ArrowLeft size={18} />
          Zurück
        </button>

        <h1 className="font-['Oswald'] text-2xl md:text-3xl font-bold text-foreground uppercase tracking-tight mb-8">
          {isNew ? 'Neues Event' : 'Event bearbeiten'}
        </h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Image Upload */}
          <div>
            <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">
              Bild
            </label>
            <div className="border border-border bg-card">
              {formData.image ? (
                <div className="relative">
                  <img 
                    src={formData.image.startsWith('/') ? `${API_URL}${formData.image}` : formData.image}
                    alt="Event"
                    className="w-full h-48 object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, image: '' }))}
                    className="absolute top-2 right-2 p-1 bg-black/50 hover:bg-black/70 text-white transition-colors"
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="w-full h-48 flex flex-col items-center justify-center gap-2 text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors"
                >
                  {uploading ? (
                    <div className="w-6 h-6 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <ImageIcon size={32} />
                      <span className="text-sm">Bild hochladen</span>
                      <span className="text-xs">Max. 5MB (JPEG, PNG, WebP)</span>
                    </>
                  )}
                </button>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={handleImageUpload}
              className="hidden"
            />
          </div>

          {/* Title */}
          <div>
            <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">
              Titel *
            </label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              placeholder="Event-Titel"
              className="rounded-none text-lg"
              required
              data-testid="event-title-input"
            />
          </div>

          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">
                Datum *
              </label>
              <DatePicker
                value={formData.date}
                onChange={(value) => setFormData({...formData, date: value})}
                placeholder="Datum wählen"
                data-testid="event-date-input"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">
                Uhrzeit
              </label>
              <Input
                type="time"
                value={formData.time}
                onChange={(e) => setFormData({...formData, time: e.target.value})}
                className="rounded-none"
                data-testid="event-time-input"
              />
            </div>
          </div>

          {/* Location */}
          <div>
            <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">
              Ort
            </label>
            <Input
              value={formData.location}
              onChange={(e) => setFormData({...formData, location: e.target.value})}
              placeholder="z.B. Clubhaus, Rennstrecke..."
              className="rounded-none"
              data-testid="event-location-input"
            />
          </div>

          {/* Link */}
          <div>
            <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">
              Link (URL)
            </label>
            <Input
              type="url"
              value={formData.link}
              onChange={(e) => setFormData({...formData, link: e.target.value})}
              placeholder="https://..."
              className="rounded-none"
              data-testid="event-link-input"
            />
          </div>

          {/* Link Text */}
          {formData.link && (
            <div>
              <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">
                Link-Button Text
              </label>
              <select
                value={formData.link_text}
                onChange={(e) => setFormData({...formData, link_text: e.target.value})}
                className="w-full p-3 bg-background border border-border text-foreground rounded-none"
                data-testid="event-link-text-select"
              >
                {linkTextOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Lead Text */}
          <div>
            <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">
              Lead (Kurztext)
            </label>
            <Textarea
              value={formData.lead}
              onChange={(e) => setFormData({...formData, lead: e.target.value})}
              placeholder="Kurze Einleitung oder Zusammenfassung..."
              className="rounded-none min-h-[80px]"
              data-testid="event-lead-input"
            />
          </div>

          {/* Veranstalter */}
          <div>
            <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">
              Organisator (Kontaktperson) *
            </label>
            <OrganizerSearch
              members={members}
              value={formData.organizer_id}
              onChange={(id) => setFormData({...formData, organizer_id: id})}
            />
          </div>

          {/* Booking Section */}
          <div className="pt-6 border-t border-border">
            <h3 className="font-['Oswald'] text-lg font-bold uppercase tracking-wider text-primary mb-4">
              Buchungssystem
            </h3>
            
            <div className="space-y-4">
              {/* Registration Open Toggle */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="registration_open"
                  checked={formData.registration_open}
                  onChange={(e) => setFormData({...formData, registration_open: e.target.checked})}
                  className="w-4 h-4"
                />
                <label htmlFor="registration_open" className="text-sm">
                  Anmeldung aktivieren
                </label>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="guest_registration_enabled"
                  checked={formData.guest_registration_enabled}
                  onChange={(e) => setFormData({...formData, guest_registration_enabled: e.target.checked})}
                  className="w-4 h-4"
                  data-testid="guest-registration-toggle"
                />
                <label htmlFor="guest_registration_enabled" className="text-sm">
                  Gast-Anmeldung erlauben (ohne Login, auf öffentlicher Event-Seite)
                </label>
              </div>

              {/* Public Event Link - show when guest registration is enabled */}
              {id && formData.guest_registration_enabled && (
                <PublicEventLink eventId={id} />
              )}

              {/* Max Participants */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">
                    Max. Teilnehmer
                  </label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.max_participants}
                    onChange={(e) => setFormData({...formData, max_participants: e.target.value})}
                    placeholder="Unbegrenzt"
                    className="rounded-none"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Leer = unbegrenzt</p>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">
                    Anmeldefrist
                  </label>
                  <DatePicker
                    value={formData.registration_deadline}
                    onChange={(value) => setFormData({...formData, registration_deadline: value})}
                    placeholder="Keine Frist"
                  />
                </div>
              </div>

              {/* Price */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">
                    Preis (CHF)
                  </label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    placeholder="0.00"
                    className="rounded-none"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">
                    Preis-Label
                  </label>
                  <Input
                    value={formData.price_label}
                    onChange={(e) => setFormData({...formData, price_label: e.target.value})}
                    placeholder="z.B. 'pro Person', 'inkl. Essen'"
                    className="rounded-none"
                  />
                </div>
              </div>

              {/* Payment Required */}
              {parseFloat(formData.price) > 0 && (
                <div className="p-4 bg-muted/30 border border-border">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="payment_required"
                      checked={formData.payment_required}
                      onChange={(e) => setFormData({...formData, payment_required: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <label htmlFor="payment_required" className="text-sm font-medium">
                      Zahlung bei Anmeldung erforderlich (TWINT/Kreditkarte)
                    </label>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2 ml-7">
                    Wenn aktiviert, müssen Teilnehmer bei der Anmeldung direkt bezahlen.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Visibility Section */}
          <div className="pt-6 border-t border-border">
            <h3 className="font-['Oswald'] text-lg font-bold uppercase tracking-wider text-primary mb-4">
              Sichtbarkeit
            </h3>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="visible_members"
                  checked={formData.visible_members}
                  onChange={(e) => setFormData({...formData, visible_members: e.target.checked})}
                  className="w-4 h-4 mt-0.5"
                />
                <div>
                  <label htmlFor="visible_members" className="text-sm font-medium block">
                    Im Mitgliederbereich anzeigen
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Event wird für eingeloggte Mitglieder sichtbar
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="visible_public"
                  checked={formData.visible_public}
                  onChange={(e) => setFormData({...formData, visible_public: e.target.checked})}
                  className="w-4 h-4 mt-0.5"
                />
                <div>
                  <label htmlFor="visible_public" className="text-sm font-medium block">
                    Auf Webseite anzeigen
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Event wird öffentlich auf shrt.ch angezeigt (iframe)
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* WYSIWYG Content */}
          <div>
            <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">
              Inhalt
            </label>
            <WysiwygEditor
              value={formData.content}
              onChange={(value) => setFormData({...formData, content: value})}
              placeholder="Ausführliche Beschreibung..."
            />
          </div>

          {/* Dateianhänge */}
          {!isNew && (
            <div className="pt-6 border-t border-border">
              <h3 className="font-['Oswald'] text-lg font-bold uppercase tracking-wider text-primary mb-4">
                Dateianhänge
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                PDFs und Bilder, die als Download-Links auf der Event-Seite und im Newsletter erscheinen.
              </p>
              
              {/* Upload button */}
              <div className="mb-4">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,.webp,.gif"
                    onChange={handleAttachmentUpload}
                    className="hidden"
                    data-testid="attachment-file-input"
                  />
                  <div className="inline-flex items-center gap-2 px-4 py-2 border border-dashed border-border hover:border-primary text-sm text-muted-foreground hover:text-foreground transition-colors">
                    {uploadingAttachment ? (
                      <><Loader2 size={16} className="animate-spin" /> Wird hochgeladen...</>
                    ) : (
                      <><Upload size={16} /> Dateien hinzufügen (PDF, Bilder)</>
                    )}
                  </div>
                </label>
              </div>

              {/* Attachment list */}
              {attachments.length > 0 && (
                <div className="space-y-2">
                  {attachments.map(att => (
                    <div key={att.id} className="flex items-center gap-3 p-3 bg-muted/20 border border-border group">
                      <div className="flex-shrink-0">
                        {att.file_type === 'pdf' ? (
                          <div className="w-8 h-8 bg-red-500/20 text-red-500 flex items-center justify-center text-xs font-bold">PDF</div>
                        ) : (
                          <img src={att.file_url} alt={att.filename} className="w-8 h-8 object-cover" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <a href={att.file_url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-foreground hover:text-primary truncate block">
                          {att.filename}
                        </a>
                        <span className="text-xs text-muted-foreground">{formatFileSize(att.file_size)}</span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="opacity-0 group-hover:opacity-100 p-1 text-muted-foreground hover:text-red-500"
                        onClick={() => handleDeleteAttachment(att.id, att.filename)}
                        data-testid={`delete-attachment-${att.id}`}
                      >
                        <X size={16} />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Rückblick Section - at the bottom */}
          <div className="pt-6 border-t border-border">
            <h3 className="font-['Oswald'] text-lg font-bold uppercase tracking-wider text-primary mb-4">
              Rückblick
            </h3>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="has_review"
                  checked={formData.has_review}
                  onChange={(e) => setFormData({...formData, has_review: e.target.checked})}
                  className="w-4 h-4 mt-0.5"
                />
                <div>
                  <label htmlFor="has_review" className="text-sm font-medium block">
                    Rückblick aktivieren
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Zeigt das Event als Rückblick mit Bildergalerie auf der Webseite an (auch vergangene Events)
                  </p>
                </div>
              </div>

              {formData.has_review && (
                <>
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">
                      Rückblick-Text
                    </label>
                    <WysiwygEditor
                      value={formData.review_text}
                      onChange={(value) => setFormData({...formData, review_text: value})}
                      placeholder="Beschreibung des Rückblicks..."
                    />
                  </div>

                  <div>
                    <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">
                      Bildergalerie
                    </label>
                    <ImageGalleryUpload
                      images={formData.review_images || []}
                      onChange={(images) => setFormData(prev => ({ ...prev, review_images: images }))}
                      folder="events/gallery"
                    />
                  </div>

                  <div>
                    <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">
                      PDF-Dateien (max. 3)
                    </label>
                    <PdfUpload
                      pdfs={formData.review_pdfs || []}
                      onChange={(pdfs) => setFormData(prev => ({ ...prev, review_pdfs: pdfs }))}
                      folder="events/review-pdfs"
                      maxFiles={3}
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(isNew ? '/events' : `/events/${id}`)}
              className="rounded-none"
            >
              Abbrechen
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-primary hover:bg-red-700 text-white rounded-none flex-1 md:flex-none md:min-w-[200px]"
              data-testid="event-submit-btn"
            >
              {saving ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                isNew ? 'Event erstellen' : 'Änderungen speichern'
              )}
            </Button>
          </div>
        </form>
      </div>

      {/* Custom styles for event content */}
      <style>{`
        .event-content h1, .event-content h2, .event-content h3 {
          font-family: 'Oswald', sans-serif;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-top: 1.5em;
          margin-bottom: 0.5em;
        }
        .event-content p {
          margin-bottom: 1em;
        }
        .event-content ul, .event-content ol {
          margin-left: 1.5em;
          margin-bottom: 1em;
        }
        .event-content a {
          color: hsl(var(--primary));
          text-decoration: underline;
        }
      `}</style>
    </Layout>
  );
}


function PublicEventLink({ eventId }) {
  const [copied, setCopied] = useState(false);
  const publicUrl = `https://member.shrt.ch/public/event/${eventId}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(publicUrl).then(() => {
      setCopied(true);
      toast.success('Link kopiert!');
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="flex items-center gap-2 ml-6 mt-1 p-2 bg-muted/50 border border-border rounded-sm" data-testid="public-event-link">
      <ExternalLink size={14} className="text-muted-foreground flex-shrink-0" />
      <code className="text-xs text-primary truncate flex-1">{publicUrl}</code>
      <button type="button" onClick={handleCopy}
        className="flex items-center gap-1 px-2 py-1 text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90 flex-shrink-0"
        data-testid="copy-public-link-btn">
        {copied ? <Check size={12} /> : <Copy size={12} />}
        {copied ? 'Kopiert' : 'Kopieren'}
      </button>
    </div>
  );
}


function OrganizerSearch({ members, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  const selectedMember = members.find(m => m.id === value);
  const filtered = search
    ? members.filter(m => m.name.toLowerCase().includes(search.toLowerCase()))
    : members;

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={ref} className="relative" data-testid="event-organizer-select">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-3 bg-background border border-border text-foreground text-left"
      >
        <span className={selectedMember ? '' : 'text-muted-foreground'}>
          {selectedMember ? selectedMember.name : '-- Organisator wählen --'}
        </span>
        <ChevronsUpDown size={16} className="text-muted-foreground" />
      </button>

      {open && (
        <div className="absolute z-50 w-full mt-1 bg-card border border-border shadow-lg max-h-64 flex flex-col">
          <div className="flex items-center gap-2 p-2 border-b border-border">
            <Search size={14} className="text-muted-foreground" />
            <input
              type="text"
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="flex-1 bg-transparent border-none outline-none text-sm text-foreground placeholder:text-muted-foreground"
              placeholder="Name suchen..."
              data-testid="organizer-search-input"
            />
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 && (
              <div className="p-3 text-sm text-muted-foreground text-center">Kein Mitglied gefunden</div>
            )}
            {filtered.map(m => (
              <button
                key={m.id}
                type="button"
                onClick={() => { onChange(m.id); setOpen(false); setSearch(''); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-muted/50 flex items-center gap-2 ${m.id === value ? 'bg-muted font-semibold' : ''}`}
                data-testid={`organizer-option-${m.id}`}
              >
                {m.id === value && <Check size={14} className="text-primary" />}
                <span className={m.id === value ? '' : 'ml-5'}>{m.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
